'use client';

import { useCallback, useEffect, useState } from 'react';
import type maplibregl from 'maplibre-gl';

/**
 * The bar never draws wider than this; the rounded distance shrinks it to fit.
 * Exported so the unit test derives its geometry from the same number rather
 * than duplicating it - a copy drifts, and the assertion that the bar uses the
 * full width would then pass while the bar was 96 of 120px.
 */
export const MAX_BAR_PX = 96;

export interface MapScale {
  /** The rounded ground distance the bar spans. */
  meters: number;
  /** How wide to draw the bar so it spans exactly `meters`. */
  widthPx: number;
}

/**
 * Round down to the nearest 1/2/3/5 x 10^n - the distances a chart reader
 * expects on a scale bar. An unrounded "437 M" is technically accurate and
 * operationally useless: the bar exists so an operator can estimate a distance
 * at a glance, which needs a number they can multiply in their head.
 */
function roundDistance(meters: number): number {
  const pow10 = 10 ** Math.floor(Math.log10(meters));
  const leading = meters / pow10;
  const step = leading >= 10 ? 10 : leading >= 5 ? 5 : leading >= 3 ? 3 : leading >= 2 ? 2 : 1;
  return step * pow10;
}

/**
 * Metric. That matches the COP's ground distances - a geofence radius and the
 * drone card's Alt AGL tile are both metres - but NOT its altitude UI, which
 * follows the org's unit and defaults to feet (DEFAULT_ALTITUDE_PROFILE.unit
 * is 'ft' in lib/altitude.ts, an FAA-aligned 400 ft ceiling). So a default US
 * org reads a launch ceiling in FT and this bar in M.
 *
 * Left metric for now because a scale bar is a ground measurement, not an
 * altitude - but that is an open question rather than a settled call, and it
 * wants an operator's answer. maplibre's own ScaleControl carries
 * metric | imperial | nautical if the org unit should drive this too.
 */
export function formatScaleDistance(meters: number): string {
  return meters >= 1000 ? `${meters / 1000} KM` : `${meters} M`;
}

/**
 * Measures across the horizontal centre line of the canvas rather than at a
 * fixed latitude: under Web Mercator the metres-per-pixel scale changes with
 * latitude, so only a bar measured at what the operator is actually looking at
 * stays honest as they pan north or south.
 *
 * Exported for the unit test - the geometry is the part worth pinning down.
 */
export function measureScale(map: maplibregl.Map): MapScale | null {
  const canvas = map.getCanvas();
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (!width || !height) return null;

  const y = height / 2;
  const left = map.unproject([width / 2 - MAX_BAR_PX / 2, y]);
  const right = map.unproject([width / 2 + MAX_BAR_PX / 2, y]);
  const spanMeters = left.distanceTo(right);
  // A map that has not laid out yet, or one unprojected past the poles, can
  // hand back 0/NaN - draw nothing rather than a bar that lies.
  if (!Number.isFinite(spanMeters) || spanMeters <= 0) return null;

  const meters = roundDistance(spanMeters);
  return { meters, widthPx: Math.round(MAX_BAR_PX * (meters / spanMeters)) };
}

interface MapScaleBarProps {
  /** Null until MapLibre has been constructed; the bar renders once it exists. */
  map: maplibregl.Map | null;
  /**
   * "lat, lng" under the pointer, or null when the pointer is off the map.
   *
   * It arrives here because the count strip that used to carry it was cut: every
   * other field on that strip said something the operator could already read
   * somewhere else (FLYING/TRACKED is the strip board directly under it,
   * MISSIONS is the badge on the top bar's Missions key, TRAILS is a map-key
   * fact), and this was the one that was not. A ground measurement belongs
   * beside the other ground measurement rather than in a strip of its own.
   */
  cursorCoords?: string | null;
}

/**
 * Ground-distance reference for the COP map, and the cursor readout with it -
 * the coordinates are drawn INSIDE this row, never as a second one.
 *
 * NOT standalone chrome: it renders INSIDE the map key's pill (MapLegend owns
 * the border, background, blur and the h-7/28px height) as the reading to the
 * right of the key button, and it draws only the divider between the two.
 * Dropped onto another map surface as it stands it would be transparent 10px
 * text with a stray left border, and self-stretch would be inert - give it a
 * flex host with chrome, or restore the pill classes on a wrapper. Rendering
 * the divider here rather than in the pill is deliberate: a map with no scale
 * yet then leaves a clean button rather than a pill with a dangling separator.
 * DroneMapControls' corner height and AirspacePanel's bottom offset are both
 * measured against that 28px.
 */
export function MapScaleBar({ map, cursorCoords = null }: MapScaleBarProps) {
  const [scale, setScale] = useState<MapScale | null>(null);
  const [measuredFor, setMeasuredFor] = useState<maplibregl.Map | null>(null);

  // Take the first measurement during render, not in the effect below. The map
  // is already built by the time it is handed down, so measuring here puts the
  // bar on screen correct in its first paint instead of a frame later - and it
  // leaves the effect doing only what an effect should, which is subscribe.
  if (map !== measuredFor) {
    setMeasuredFor(map);
    setScale(map ? measureScale(map) : null);
  }

  const update = useCallback(() => {
    if (!map) return;
    const next = measureScale(map);
    if (!next) return;
    // Returning the previous object makes React bail out of the render. 'move'
    // fires on every frame of a pan and a pure translation almost never changes
    // either number, so without this the scale bar would re-render the whole
    // way across a drag for nothing.
    setScale((prev) =>
      prev && prev.meters === next.meters && prev.widthPx === next.widthPx ? prev : next,
    );
  }, [map]);

  useEffect(() => {
    if (!map) return;
    map.on('move', update);
    map.on('resize', update);
    return () => {
      map.off('move', update);
      map.off('resize', update);
    };
  }, [map, update]);

  // Either reading is enough to draw the row. The coordinates are the one fact
  // rescued from the cut count strip, and `measureScale` returns null on a
  // zero-size canvas or a non-finite unproject - there is no reason for the
  // rarer of the two to take the other down with it.
  if (!scale && !cursorCoords) return null;

  return (
    <div
      // pointer-events-none: this is a readout, not a control - no click, no
      // focus, no hover. Without it the bar eats map clicks over its own
      // footprint, so with GO-TO armed a pick lands on nothing with no feedback,
      // and an aircraft that drifts underneath stops being selectable. Same
      // reasoning as DroneHoverChip. The pill around it is pointer-events-none
      // too and pointer-events inherits, so this is belt and braces - kept
      // because it is the bar's own footprint to disclaim, and because a future
      // host that arms its own container must not silently arm this readout.
      className="pointer-events-none flex items-center gap-2 self-stretch border-l border-subtle px-2 text-[10px] uppercase tracking-wider text-muted-foreground"
    >
      {/* role="img": the bar and its label are one graphic conveying a single
          fact, so screen readers should read that fact once, not the bare
          number. Scoped to the bracket and its number rather than to the whole
          row, because an `aria-label` on the row would swallow the coordinates
          below - the readout would be on screen and unreadable to anyone not
          looking at it. */}
      {scale && (
        <span
          role="img"
          aria-label={`Map scale: ${formatScaleDistance(scale.meters)}`}
          className="flex items-center gap-2"
        >
          {/* Left/right/bottom borders only - the classic |___| scale bracket. */}
          <span
            data-testid="map-scale-bar-line"
            className="block h-1.5 border-x border-b border-current"
            style={{ width: `${scale.widthPx}px` }}
          />
          <span className="font-mono tabular-nums">{formatScaleDistance(scale.meters)}</span>
        </span>
      )}
      {cursorCoords && (
        <>
          {scale && <span aria-hidden className="h-3 w-px shrink-0 bg-divider" />}
          <span data-testid="map-cursor-coords" className="font-mono tabular-nums">
            {cursorCoords}
          </span>
        </>
      )}
    </div>
  );
}
